import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import { EstateTaskGroup, EstateTaskInstance, PortalPermissions } from 'queries';

import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateTaskGroupRow from './estate-task/estate-task-group-row';
import { useStyles } from './task-group-accordion.styles';

type TaskGroupAccordionProps = {
  group: string;
  estateTaskInstances: EstateTaskInstance[];
  featureSubscriptionTypeLoading: boolean;
  estateTaskColumns: GridColDef[];
  groupIndex: number;
  expanded: boolean;
  onToggle: (group: string, next: boolean) => void;
  onToggleHidden: (group: string) => void;
  showHidden: boolean;
  onAddCustomTask: (group: EstateTaskGroup) => void;
};

export const TaskGroupAccordion = ({
  group,
  groupIndex,
  estateTaskInstances,
  estateTaskColumns,
  expanded,
  featureSubscriptionTypeLoading,
  showHidden,
  onToggle,
  onToggleHidden,
  onAddCustomTask,
}: TaskGroupAccordionProps) => {
  const classes = useStyles();

  const visibleRows = showHidden
    ? estateTaskInstances?.filter((instances) => instances.isHidden)
    : estateTaskInstances?.filter((instances) => !instances.isHidden);

  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  return (
    <>
      <Accordion expanded={expanded} elevation={0} onChange={() => onToggle(group, !expanded)}>
        <Box className={classes.sectionHeader}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            className={classes.accordionSummary}
          >
            <EstateTaskGroupRow group={group} groupNumber={groupIndex} />
            {!hasPortalPermissionClient && (
              <ToggleButtonGroup
                value={showHidden ? 'show-hidden-tasks' : null}
                exclusive
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleHidden(group);
                }}
              >
                <ToggleButton
                  className={classes.showHiddenTasksButton}
                  value={'show-hidden-tasks'}
                  size="small"
                >
                  Show Hidden Tasks
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Button
              className={classes.addTaskButton}
              onClick={(e) => {
                e.stopPropagation();
                onAddCustomTask(group as EstateTaskGroup);
              }}
            >
              Add Task
            </Button>
          </AccordionSummary>
        </Box>
        <AccordionDetails>
          <Box className={classes.dataGridWrapper}>
            <DataGrid
              loading={featureSubscriptionTypeLoading}
              className={classes.estateTaskDataGrid}
              columns={estateTaskColumns}
              rows={visibleRows}
              getRowHeight={() => 'auto'}
              rowHeight={60}
              disableColumnReorder
              disableRowSelectionOnClick
              disableColumnMenu
              keepNonExistentRowsSelected
              hideFooter
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Divider />
    </>
  );
};
